import React, {useEffect, useState} from 'react';
import PropTypes from 'prop-types';
import _clone from 'lodash/clone';
import GooglePlacesAutocomplete from 'react-google-places-autocomplete';
import moment from 'moment';
import styles from './CommuteCalc.scss';
import {dataLayerPush, propertyViewTracker} from '../../lib/tracking';
import Tooltip from '../Tooltip/Tooltip';
import TooltipText from '../../constants/TooltipText';

const compactDuration = (durationSecs) => {
  let d = moment.duration(durationSecs, 'seconds');
  let ret = [];
  if (d.months()) ret.push(d.months() + 'mth');
  if (d.weeks()) ret.push(d.weeks() + 'w');
  if (d.days()) ret.push(d.weeks() ? (d.days() - (d.weeks() * 7)) + 'd' : d.days() + 'd');
  if (d.hours()) ret.push(d.hours() + 'h');
  if (d.minutes()) ret.push(d.minutes() + 'm');
  return ret.join(' ');
}

function CommuteCalc(props) {
  let commuteService;
  const [value, setValue] = useState(null);
  const [display, setDisplay] = useState(false);
  const [driving, setDriving] = useState('-');
  const [walking, setWalking] = useState('-');
  const [biking, setBiking] = useState('-');
  const [transit, setTransit] = useState('-');
  const [isMapScriptLoaded, setMapScriptLoaded] = useState(false);

  useEffect(() => {
    setDisplay(true);
  }, []);

  if(typeof window === 'object') {
    useEffect(() => {
      if(window.google && window.google.maps) {
        setMapScriptLoaded(true);
      }
    }, [window.google]);
  }

  useEffect(() => {
    // get the commute data
    if (value && typeof window != 'undefined' && window.google && window.google.maps) {
      if (!commuteService) commuteService = new google.maps.DistanceMatrixService();
      let drv = {
        origins: [props.origin],
        destinations: [value.label],
        travelMode: 'DRIVING'
      };
      commuteService.getDistanceMatrix(drv, (response, status) => updateCommute(google.maps.TravelMode.DRIVING, response, status));

      let wlk = _clone(drv);
      wlk.travelMode = 'WALKING';
      commuteService.getDistanceMatrix(wlk, (response, status) => updateCommute(google.maps.TravelMode.WALKING, response, status));

      let bike = _clone(drv);
      bike.travelMode = 'BICYCLING';
      commuteService.getDistanceMatrix(bike, (response, status) => updateCommute(google.maps.TravelMode.BICYCLING, response, status));

      let bus = _clone(drv);
      bus.travelMode = 'TRANSIT';
      commuteService.getDistanceMatrix(bus, (response, status) => updateCommute(google.maps.TravelMode.TRANSIT, response, status));

      let gaEvent = {
        event: 'commuteClick',
        origin: props.origin,
        destination: value.label,
      };
      dataLayerPush(gaEvent);

      propertyViewTracker('commute calculator', props.property);
    }
    else if (value == null) {
      // clear the fields
      setDriving('-');
      setWalking('-');
      setBiking('-');
      setTransit('-');
    }
  }, [value]);

  const updateCommute = (mode, response, status) => {
    if (status == 'OK') {
      let duration = '-';
      if (response.rows[0].elements[0].status == 'OK') duration = compactDuration(response.rows[0].elements[0].duration.value);
      switch (mode) {
        case google.maps.TravelMode.DRIVING:
          setDriving(duration);
          break;
        case google.maps.TravelMode.WALKING:
          setWalking(duration);
          break;
        case google.maps.TravelMode.BICYCLING:
          setBiking(duration);
          break;
        case google.maps.TravelMode.TRANSIT:
          setTransit(duration);
          break;
      }
    }
  }

  const config = {
    id:'commute_id',
    inputId:'commute_inputid',
    instanceId:'commute_instanceid',
    className: `${styles.commute_inputContainer}`,
    classNamePrefix: `${styles['commute_inputContainer']}`,
    openMenuOnClick: false,
    value,
    onChange: setValue,
    placeholder: 'Where to?',
    isClearable: true,
    isDisabled: false,
    components: {
      DropdownIndicator:() => null,
      IndicatorSeparator:() => null,
    },
    styles: {
      clearIndicator: (provided, state) => ({
        ...provided,
        cursor: 'pointer',
      }),
    }
  };

  const requestParams = {
    componentRestrictions: {
      country: ['au']
    }
  };

  return (
    <div className={styles.commuteCalc}>
      <div className="d-flex align-items-top">
        <span className="bold600 mr-2 mb-3">Commute Calculator</span>
        {display && <Tooltip
          id="commuteCalculator"
          text={TooltipText.COMMUTE_CALCULATOR}
          position="bottom"
          iconClass={styles.commuteCalculatorIcon}
          popperClass={styles.commuteCalculatorPopper}
        />}
      </div>
      {display &&
        <div className="d-md-flex">
          <div className="flex-grow-1">{isMapScriptLoaded && <GooglePlacesAutocomplete selectProps={config} autocompletionRequest={requestParams} />}</div>
          <div className="d-flex justify-content-around mt-3 mt-md-0">
            <div className={styles.drive}>{driving}</div>
            <div className={styles.train}>{transit}</div>
            <div className={styles.bike}>{biking}</div>
            <div className={styles.walk}>{walking}</div>
          </div>
        </div>
      }
    </div>
  );
}

CommuteCalc.propTypes = {
  origin: PropTypes.string.isRequired,
};

export default CommuteCalc;
